import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Pencil, Trash2, MessageSquare, Loader2, Copy, Send, Search,
} from 'lucide-react';
import { waNumber } from '@/utils/phone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_STYLES = {
  Payments: 'bg-emerald-100 text-emerald-700',
  Documents: 'bg-sky-100 text-sky-700',
  'Pre-departure': 'bg-amber-100 text-amber-700',
  'During trip': 'bg-violet-100 text-violet-700',
  'Post-trip': 'bg-teal-100 text-teal-700',
  'Lead nurturing': 'bg-rose-100 text-rose-700',
  Operational: 'bg-slate-100 text-slate-600',
};

const fillBody = (body, values) =>
  (body || '').replace(/\{(\w+)\}/g, (m, k) => {
    const v = values[k];
    return v != null && String(v).trim() !== '' ? String(v) : m;
  });

// ---- Create / Edit dialog --------------------------------------------------
const MessageTemplateDialog = ({ open, onOpenChange, initial, categories, variables, onSaved }) => {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || '');
  const [category, setCategory] = useState(initial?.category || 'Operational');
  const [body, setBody] = useState(initial?.body || '');
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const insertVar = (token) => {
    const el = bodyRef.current;
    const chip = `{${token}}`;
    if (!el) { setBody((b) => b + chip); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + chip + body.slice(end);
    setBody(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + chip.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const save = async () => {
    if (!name.trim()) { toast.error('Template name is required'); return; }
    if (!body.trim()) { toast.error('Message body is required'); return; }
    setSaving(true);
    const payload = { name: name.trim(), category, body: body.trim(), channel: 'whatsapp' };
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/message-templates/${initial.id}`, payload);
        toast.success('Template updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/message-templates`, payload);
        toast.success('Template created');
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="message-template-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Message Template' : 'New Message Template'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Template name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Partial payment received" className="h-10" data-testid="msg-template-name-input" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Category</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-10" data-testid="msg-template-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`msg-cat-opt-${c}`}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Message</Label>
            <Textarea ref={bodyRef} value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="Type your message. Click a variable below to insert it." className="text-sm leading-relaxed" data-testid="msg-template-body-input" />
            <p className="text-xs text-slate-400 mt-2 mb-1.5">Click to insert a variable:</p>
            <div className="flex flex-wrap gap-1.5" data-testid="msg-template-variable-chips">
              {variables.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onClick={() => insertVar(v.token)}
                  title={v.label}
                  className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 text-[11px] font-mono hover:bg-teal-100 transition-colors"
                  data-testid={`msg-var-chip-${v.token}`}
                >
                  {`{${v.token}}`}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="msg-template-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="msg-template-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Use template on a trip ------------------------------------------------
const UseTemplateDialog = ({ open, onOpenChange, template }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [trip, setTrip] = useState(null);
  const [values, setValues] = useState({});
  const [company, setCompany] = useState({});

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/company`).then((r) => setCompany(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const id = setTimeout(async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/trips`, { params: { search: query.trim(), limit: 8 } });
        setResults(Array.isArray(r.data) ? r.data : []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [query]);

  const vars = template?.variables || [];

  const selectTrip = (t) => {
    setTrip(t);
    setResults([]);
    setQuery(t.guestName || t.displayTripId || '');
    const auto = {
      client_name: t.guestName || '',
      destination: t.destination || '',
      departure_date: t.travelStartDate || '',
      booking_ref: t.displayTripId || t.tripId || '',
      outstanding_amount: t.outstanding != null ? String(t.outstanding) : '',
      agent_phone: company.phone || '',
      agent_name: company.companyName || '',
    };
    setValues((prev) => {
      const next = { ...prev };
      vars.forEach((v) => { if (auto[v] != null && auto[v] !== '') next[v] = auto[v]; });
      return next;
    });
  };

  const finalMessage = useMemo(() => fillBody(template?.body, values), [template, values]);
  const phone = waNumber(trip?.countryCode, trip?.phone);

  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(finalMessage); toast.success('Message copied'); }
    catch { toast.error('Copy failed'); }
  };
  const openWhatsApp = () => {
    const base = phone ? `https://wa.me/${phone}` : 'https://wa.me/';
    window.open(`${base}?text=${encodeURIComponent(finalMessage)}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="use-template-dialog">
        <DialogHeader>
          <DialogTitle>Use “{template?.name}”</DialogTitle>
          <DialogDescription>Pick a trip to auto-fill variables, then send via WhatsApp.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Find a trip / traveller</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={query} onChange={(e) => { setQuery(e.target.value); setTrip(null); }} placeholder="Name, Trip ID or destination…" className="h-10 pl-9" data-testid="use-template-trip-search" />
            </div>
            {results.length > 0 && (
              <div className="mt-1 rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="use-template-trip-results">
                {results.map((t) => (
                  <button key={t.id || t.tripId} type="button" onClick={() => selectTrip(t)} className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0" data-testid={`use-template-trip-${t.displayTripId || t.tripId}`}>
                    <p className="text-sm font-medium text-slate-800">{t.guestName || 'Trip'} <span className="text-xs text-slate-400 font-mono ml-1">{t.displayTripId || ''}</span></p>
                    <p className="text-xs text-slate-500">{t.destination || ''}{t.phone ? ` · ${t.phone}` : ''}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {vars.length > 0 && (
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Variables</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="use-template-variables">
                {vars.map((v) => (
                  <div key={v}>
                    <Label className="text-xs text-slate-500 mb-1 block font-mono">{`{${v}}`}</Label>
                    <Input value={values[v] || ''} onChange={(e) => setValues((p) => ({ ...p, [v]: e.target.value }))} placeholder={v.replace(/_/g, ' ')} className="h-9 text-sm" data-testid={`use-template-var-${v}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Preview</Label>
            <div className="rounded-xl bg-[#e7ffdb] border border-emerald-200 px-4 py-3 text-sm text-slate-800 whitespace-pre-line leading-relaxed" data-testid="use-template-preview">
              {finalMessage}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={copyMsg} data-testid="use-template-copy-btn"><Copy className="h-4 w-4 mr-1.5" /> Copy</Button>
          <Button onClick={openWhatsApp} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="use-template-whatsapp-btn"><Send className="h-4 w-4 mr-1.5" /> Open WhatsApp</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Messages tab ----------------------------------------------------------
const MessagesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [meta, setMeta] = useState({ categories: [], variables: [] });
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [useTarget, setUseTarget] = useState(null);

  const load = async () => {
    try {
      const [t, m] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/message-templates`),
        axios.get(`${BACKEND_URL}/api/message-templates/meta`),
      ]);
      setTemplates(Array.isArray(t.data) ? t.data : []);
      setMeta(m.data || { categories: [], variables: [] });
    } catch {
      toast.error('Failed to load message templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [t, m] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/message-templates`),
          axios.get(`${BACKEND_URL}/api/message-templates/meta`),
        ]);
        if (!active) return;
        setTemplates(Array.isArray(t.data) ? t.data : []);
        setMeta(m.data || { categories: [], variables: [] });
      } catch {
        if (active) toast.error('Failed to load message templates');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/message-templates/${deleteTarget.id}`);
      toast.success('Template deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const allCategories = meta.categories || [];
  const filtered = activeCat === 'all' ? templates : templates.filter((t) => t.category === activeCat);
  const orderedGroups = useMemo(() => {
    const g = {};
    filtered.forEach((t) => { (g[t.category] = g[t.category] || []).push(t); });
    const cats = meta.categories || [];
    return cats.filter((c) => (g[c] || []).length).map((c) => ({ cat: c, rows: g[c] }));
  }, [filtered, meta.categories]);

  return (
    <div className="space-y-4" data-testid="templates-messages-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-500">{templates.length} WhatsApp message template{templates.length === 1 ? '' : 's'}</p>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-message-template-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Template
        </Button>
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2" data-testid="message-category-filter">
        <CatPill label="All" active={activeCat === 'all'} onClick={() => setActiveCat('all')} testId="msg-cat-filter-all" />
        {allCategories.map((c) => (
          <CatPill key={c} label={c} active={activeCat === c} onClick={() => setActiveCat(c)} testId={`msg-cat-filter-${c}`} />
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No templates in this category.</div>
      ) : (
        <div className="space-y-6">
          {orderedGroups.map((grp) => {
            const cat = grp.cat;
            const rows = grp.rows;
            const catStyle = CATEGORY_STYLES[cat] || CATEGORY_STYLES.Operational;
            return (
              <div key={cat} data-testid={`message-group-${cat}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`${catStyle} border-0 text-[11px]`}>{cat}</Badge>
                  <span className="text-xs text-slate-400">{rows.length}</span>
                </div>
                <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                  {rows.map((t, i) => (
                    <div key={t.id} className={`flex items-start gap-3 px-4 py-3 ${i > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50`} data-testid={`message-template-row-${t.id}`}>
                      <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                        <MessageSquare className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-800 truncate" data-testid={`message-row-name-${t.id}`}>{t.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2 whitespace-pre-line">{t.body}</p>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <Button variant="ghost" size="sm" onClick={() => setUseTarget(t)} className="text-emerald-600 hover:text-emerald-700" title="Use on a trip" data-testid={`use-template-${t.id}`}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setDialogOpen(true); }} className="text-slate-500 hover:text-teal-600" title="Edit" data-testid={`edit-message-template-${t.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(t)} className="text-slate-500 hover:text-rose-600" title="Delete" data-testid={`delete-message-template-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialogOpen && (
        <MessageTemplateDialog
          open
          onOpenChange={setDialogOpen}
          initial={editing}
          categories={meta.categories || []}
          variables={meta.variables || []}
          onSaved={load}
        />
      )}

      {useTarget && (
        <UseTemplateDialog open onOpenChange={(o) => !o && setUseTarget(null)} template={useTarget} />
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-message-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This message template will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-message-template-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-message-template-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const CatPill = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
      active ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
    }`}
  >
    {label}
  </button>
);

export default MessagesTab;
